<template>
  <div
    v-if="lastFlashcardState && !userDisabled(identifier)"
    class="resume-widget-container"
  >
    <dashboard-widget-header
      :icon="widgetIcon"
      :title="config.name"
      :show-close="userHideable"
      @remove-requested="removeRequested(identifier)"
    />
    <button
      class="widget-content"
      @click="widgetAction"
    >
      <div class="widget-image">
        <div
          v-if="locked"
          class="locked-badge"
        >
          <icon
            class="lock-icon"
            name="lock"
            variant="white"
          />
        </div>
        <img
          v-if="widgetAttachment"
          class="image"
          :src="widgetAttachment.url"
          alt=""
          role="presentation"
        >
      </div>
      <div class="category">
        <div class="category-path">
          <span
            v-for="(path, index) in categoryPath"
            :key="path"
          >
            <icon
              v-if="index > 0"
              class="path-connector"
              name="arrow"
            />
            {{ path }}
          </span>
        </div>
        <div class="category-progress">
          <tagged-progress-bar
            v-if="!isQuizCategory"
            :green="flashcardCounts.green"
            :yellow="flashcardCounts.yellow"
            :red="flashcardCounts.red"
            :answered="flashcardCounts.answered"
            :total="flashcardCounts.total"
          />
          <pure-progress-bar
            v-else
            :answered="flashcardCounts.answered"
            :total="flashcardCounts.total"
          />
          <span class="progress-label"> {{ categoryProgressLabel }}</span>
        </div>
      </div>
      <icon
        class="arrow-icon"
        name="arrow"
      />
    </button>
  </div>
</template>

<script>
import DashboardWidgetHeader from '@/components/dashboard-widgets/header';
import Icon from '@/components/singletons/icon';
import PureProgressBar from '@components/progress/PureProgressBar';
import TaggedProgressBar from '@components/progress/TaggedProgressBar';

import DashboardWidgetMixin from '@/mixins/dashboardWidget';
import WidgetMixin from '@/mixins/widget';

export default {
  components: {DashboardWidgetHeader, Icon, PureProgressBar, TaggedProgressBar},
  mixins: [DashboardWidgetMixin, WidgetMixin],
  props: {
    config: {
      type: Object,
    },
    dashboard: {
      type: Object,
    },
    identifier: {
      type: Number,
    },
    userHideable: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    category() {
      return this.$store.getters['categories/categories'][this.lastFlashcardState.categoryId];
    },
    categoryPath() {
      if (this.isQuizCategory) {
        return [this.category.name];
      }
      const categoryIds = this.category.path.split('.');
      return categoryIds.map(id => this.$store.getters['categories/categories'][id]?.name).filter(id => id);
    },
    categoryProgressLabel() {
      return `${this.flashcardCounts.answered}/${this.flashcardCounts.total}`;
    },
    flashcardCounts() {
      // This is a slimmed down version of the function in the categories mixin since we will only always be dealing with 1 category
      const categoryStates = this.flashcardStates.filter(state => state.categoryId === this.category.id);
      const counts = {
        total: this.category.counts.total,
      };

      if (this.isQuizCategory) {
        counts.answered = categoryStates.length;
      } else {
        const flashcardStatusIds = {
          green: this.$store.getters['flashcardStatuses/greenFlashcardStatus'].id,
          yellow: this.$store.getters['flashcardStatuses/yellowFlashcardStatus'].id,
          red: this.$store.getters['flashcardStatuses/redFlashcardStatus'].id,
        };

        counts.green = categoryStates.filter(state => state.flashcardStatusId == flashcardStatusIds.green).length;
        counts.yellow = categoryStates.filter(state => state.flashcardStatusId == flashcardStatusIds.yellow).length;
        counts.red = categoryStates.filter(state => state.flashcardStatusId == flashcardStatusIds.red).length;
        counts.answered = counts.green + counts.yellow + counts.red;
      }
      return counts;
    },
    flashcardStates() {
      const states = this.$store.getters['flashcardStates/flashcardStateList'];
      return states.sort((a, b) => b.mobileCreatedAt - a.mobileCreatedAt);
    },
    isQuizCategory() {
      const quizCategories = [...this.$store.getters['categoryTypes/quizCategoryTypes'], this.$store.getters['categoryTypes/userQuizCategoryType']];
      return quizCategories.map(cat => cat.id).includes(this.category.categoryTypeId);
    },
    lastFlashcardState() {
      // We .find here to make sure we grab the last state with a category id. Otherwise, we won't have a category to return to
      return this.flashcardStates.find(state => {
        const category = state.categoryId && this.$store.getters['categories/categories'][state.categoryId];
        return category && this.supportedCategoryTypeIds.includes(category.categoryTypeId);
      });
    },
    multipleChoiceCategoryTypes() {
      // similar to the logic in categories mixin resourceCategories()
      // but just omitting the 2 problematic category types here
      // instead of working through all nav items
      return this.$store.getters['categoryTypes/multipleChoiceCategoryTypes'].filter(ct =>{
        return !ct.name.match(/onboarding|decisiontree|tour/);
      });
    },
    supportedCategoryTypeIds() {
      const multipleChoice = this.multipleChoiceCategoryTypes;
      const quiz = this.$store.getters['categoryTypes/quizCategoryTypes'];
      const userQuiz = this.$store.getters['categoryTypes/userQuizCategoryType'];
      return [...multipleChoice, ...quiz, userQuiz].map(ct => ct.id);
    },
    locked() {
      return !this.$access(this.$store, 'category', this.category.id);
    },
    widgetAttachment() {
      return this.$store.getters['attachments/get'][this.config.config.resumeCardAttachmentId];
    },
  },
  created() {
    if (this.config?.config?.resumeCardAttachmentId) {
      this.$store.dispatch('attachments/fetch', { ids: [this.config.config.resumeCardAttachmentId] });
    }
  },
  methods: {
    widgetAction() {
      if (this.locked) {
        this.$store.dispatch('ui/toggleUpsellModal', { show: true, source: { type: 'Resume Widget', value: this.dashboard.id } });
      } else {
        this.$analytics.events.viewedWidget(this.config, null, null, this.dashboard);
        let routeName;
        const completed = this.flashcardCounts.answered === this.flashcardCounts.total;
        if (this.isQuizCategory) {
          routeName = completed ? 'quizzes#results' : 'quizzes#flashcard';
        } else {
          routeName = completed ? 'categories#complete' : 'categories#flashcard';
        };

        const route = {
          name: routeName,
          params: {
            categoryId: this.category.id,
          },
        };

        this.$router.push(route);
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.resume-widget-container {
  @include padded-widget;

  .widget-content {
    display: flex;
    width: 100%;
    background: transparent;
    box-shadow: 0 2px 12px 0 rgba(34, 34, 34, 0.11);
    border-radius: 8px;
    height: rem-calc(110);
    padding: 16px;
    justify-content: stretch;
    align-items: center;
    text-align: left;

    .widget-image {
      width: rem-calc(80);
      height: rem-calc(80);
      background: #e8e8e8;
      border-radius: 6px;
      display: flex;
      justify-content: center;
      align-items: center;
      position: relative;

      .locked-badge {
        position: absolute;
        background-color: #000000;
        height: rem-calc(20);
        width: rem-calc(20);
        border-radius: 100%;
        top: -4px;
        left: -4px;

        .lock-icon {
          width: rem-calc(10);
          display: block;
          margin-top: 4px;
          margin-left: 5px;
        }
      }

      .image {
        max-height: rem-calc(48);
        max-width: rem-calc(48);
      }
    }

    .category {
      flex: 1;
      height: rem-calc(80);
      margin-left: rem-calc(16);
      display: flex;
      flex-direction: column;
      justify-content: center;

      .category-path {
        font-weight: $bold;
        margin: 0 0 rem-calc(8);

        @include breakpoint(small) {
          margin-bottom: rem-calc(4);
        }

        .path-connector {
          height: rem-calc(8);
          margin-left: rem-calc(2);
          margin-bottom: rem-calc(1);
          display: inline-block;
        }
      }

      .category-progress {
        .progress-label {
          font-size: rem-calc(12);
          color: #767676;
          margin-left: rem-calc(8);
        }
      }
    }

    .arrow-icon {
      height: 16px;

      @include breakpoint(small) {
        display: none;
      }
    }
  }
}
</style>–
